import { useRouter } from 'next/router';

import { Box, Card, Icon, Text, TextButton } from '@zillow/constellation-10';
import { IconTextAlignLeftOutline } from '@zillow/constellation-icons';

import { useGetListingDetails } from '../../../../../hooks/queries/properties';

import {
  emitClickAddDisclaimerEvent,
  emitClickEditDisclaimerEvent,
  EVENT_IDS,
} from '../../../../../analytics/events/multiFamilyCostsAndFees';
import { useAuth } from '../../../../AuthProvider';

import type { ValueOf } from 'type-fest';

const MODAL_ACTIONS = {
  ADD: 'add',
  EDIT: 'edit',
};

type ModalAction = ValueOf<typeof MODAL_ACTIONS>;

type Props = {
  disclaimerText: string | null;
  onAddDisclaimerClick?: () => void | undefined;
  isDisabled: boolean;
};

const CustomDisclaimer = ({ disclaimerText = null, onAddDisclaimerClick = () => {}, isDisabled = false }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const isUpdating = disclaimerText !== null;

  const handleDisclaimerClick = (action: ModalAction = MODAL_ACTIONS.ADD) => {
    if (onAddDisclaimerClick) {
      onAddDisclaimerClick();
    }
    if (listingDetails && user) {
      if (action === MODAL_ACTIONS.EDIT) {
        emitClickEditDisclaimerEvent({ currentProperty: listingDetails, user });
      } else {
        emitClickAddDisclaimerEvent({ currentProperty: listingDetails, user });
      }
    }
  };

  const renderBody = () => {
    return (
      <Box css={{ width: '100%' }}>
        <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text textStyle="body-bold">Custom disclaimer</Text>
          <TextButton
            onClick={() => {
              handleDisclaimerClick(MODAL_ACTIONS.EDIT);
            }}
            data-eventid={EVENT_IDS.CLICK_EDIT_DISCLAIMER}
          >
            Edit
          </TextButton>
        </Box>

        <Box css={{ marginBottom: '200' }} />
        <Text asChild>
          <p>{disclaimerText}</p>
        </Text>
      </Box>
    );
  };

  const body =
    isUpdating === false
      ? ' Add a custom disclaimer as needed. It will appear at the bottom of the costs and fees section of your listing.'
      : renderBody();

  const displayCTA = disclaimerText === null ? true : false;

  return (
    <Card data-testid="CustomDisclaimer" role="status" elevated={false} css={{ display: 'flex' }}>
      <Icon render={<IconTextAlignLeftOutline />} size="md" css={{ marginRight: '400' }} />
      <Box
        css={{
          display: 'flex',
          flexDirection: isUpdating ? 'row' : 'column-reverse',
          alignItems: 'start',
          width: '100%',
        }}
      >
        {displayCTA && (
          <TextButton
            onClick={() => handleDisclaimerClick(MODAL_ACTIONS.ADD)}
            data-eventid={EVENT_IDS.CLICK_ADD_DISCLAIMER}
            disabled={isDisabled}
          >
            Add disclaimer
          </TextButton>
        )}
        {body}
      </Box>
    </Card>
  );
};

export default CustomDisclaimer;
